import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { LuCheck } from 'react-icons/lu';
import RouteConstants from '@/features/router/RouteConstants';
import { useNewCommunicationValues } from './context/useNewCommunicationValues';
import { useNewCommunicationActions } from './context/useNewCommunicationActions';
import { WizardNav } from '../components/WizardNav';
import { SOCIAL_ICONS, SocialIcons, TSocialIconType } from '@/components/icons/SocialIcons';

export type Channel = 'whatsapp' | 'sms' | 'email';

const fmt = (value: number | null) => (value != null ? String(value) : '—');

const CHANNEL_CARDS: Array<{ id: Channel; iconkey: TSocialIconType; reach: 'phone' | 'email' }> = [
  {
    id: 'whatsapp',
    iconkey: SOCIAL_ICONS.WHATSAPP,
    reach: 'phone',
  },
  {
    id: 'sms',
    iconkey: SOCIAL_ICONS.SMS,
    reach: 'phone',
  },
  {
    id: 'email',
    iconkey: SOCIAL_ICONS.EMAIL,
    reach: 'email',
  },
];

const ChannelCard: FC<{
  id: Channel;
  iconkey: TSocialIconType;
  reach: 'phone' | 'email';
  availability: Record<Channel, boolean | undefined>;
  channels: Channel[];
  withPhone: number | null;
  withEmail: number | null;
  showCosts: boolean;
  toggleChannel: (channel: Channel) => void;
}> = ({ id, iconkey, reach, availability, channels, withPhone, withEmail, showCosts, toggleChannel }) => {
  const { t } = useTranslation('comuni');
  const history = useHistory();

  // undefined (still loading) behaves as available to avoid a flash of "not connected"
  const available = availability[id] !== false;
  const selected = channels.includes(id);
  const reachCount = reach === 'phone' ? withPhone : withEmail;
  const reachLabel = reach === 'phone' ? t('channel.contactsWithPhone') : t('channel.contactsWithEmail');

  if (!available) {
    return (
      <button
        key={id}
        type='button'
        onClick={() => history.push(RouteConstants.destinations)}
        className='relative rounded-[14px] border bg-white-100 p-6 text-left hover:border-brand-200'
      >
        <span className='flex items-center gap-3'>
          <SocialIcons type={iconkey} />
          <span className='text-[15px] font-extrabold text-primary-900'>{t(`channel.${id}`)}</span>
        </span>
        <span className='mt-4 block text-sm font-semibold text-tertiary-600'>{t('channel.notConnected')}</span>
        <span className='mt-1 block text-sm font-bold text-brand-700'>{t('channel.connectCta')}</span>
        <span className='mt-2 block text-xs text-tertiary-600'>{t('channel.connectHint', { channel: t(`channel.${id}`) })}</span>
      </button>
    );
  }

  return (
    <button
      key={id}
      type='button'
      onClick={() => toggleChannel(id)}
      className={`relative rounded-[14px] border p-6 text-left ${
        selected ? 'border-2 border-brand-600 bg-brand-50 p-[23px]' : 'bg-white-100 hover:border-brand-200'
      }`}
    >
      <span className='flex items-center gap-3'>
        {selected ? (
          <span className='flex size-[22px] items-center justify-center rounded-md bg-brand-700'>
            <LuCheck className='size-3 text-white-100' strokeWidth={3} />
          </span>
        ) : (
          <span className='size-[22px] rounded-md border-[1.5px] border-quaternary-300 bg-white-100' />
        )}
        <SocialIcons type={iconkey} />
        <span className='text-[15px] font-bold text-primary-900'>{t(`channel.${id}`)}</span>
      </span>
      <span className='mt-4 block text-sm text-tertiary-600'>
        <b className='text-base font-semibold text-primary-900'>{fmt(reachCount)}</b> {reachLabel}
      </span>
      {showCosts && reachCount != null && <span className='mt-2 block text-xs text-tertiary-600'>{t('channel.estimatedCost', { count: reachCount })}</span>}
    </button>
  );
};

export const ChannelStepContainer = () => {
  const { t } = useTranslation('comuni');

  const { channels, counts, availability, showCosts } = useNewCommunicationValues();
  const { toggleChannel, handleChannelNext, goToPreviousStep } = useNewCommunicationActions();

  return (
    <section className='rounded-2xl border p-6 w-full grid grid-cols-1'>
      <h3 className='text-[26px] font-semibold tracking-tight'>{t('channel.title')}</h3>
      <p className='mt-1.5 text-[15px] text-tertiary-600'>
        {t('channel.subtitle')}
        {counts?.total != null && <> · {t('channel.recipientsRecap', { count: counts.total })}</>}
      </p>
      <div className='mt-6 grid grid-cols-1 gap-5 md:grid-cols-3'>
        {CHANNEL_CARDS.map((card) => (
          <ChannelCard
            key={card.id}
            {...card}
            availability={availability}
            channels={channels}
            withPhone={counts?.withPhone}
            withEmail={counts?.withEmail}
            showCosts={showCosts}
            toggleChannel={toggleChannel}
          />
        ))}
      </div>
      {channels.length > 1 && <p className='mt-4 text-sm text-tertiary-600'>{t('channel.multiNote')}</p>}
      <WizardNav primaryText={t('composer.next')} onPrimary={handleChannelNext} primaryDisabled={channels.length === 0} onBack={goToPreviousStep} />
    </section>
  );
};
